import React, { lazy, Suspense, useMemo, useState } from "react";
import cloneDeep from "lodash/cloneDeep";
import "./GradePage.css";
import "./PackPage.css";
import "../MathFactsGenerator/Workbench.css";
import { FOOTER_GROUPS, findBySlug, footerGroupOf } from "../Shared/Seo/routeSeo.ts";
import { Seo } from "../Shared/Seo/Seo.tsx";
import { useCanPreviewPdf } from "../Shared/Pdf/useCanPreviewPdf.ts";
import { DefaultSettings } from "../MathFactsGenerator/Models.ts";
import { SettingsProvider } from "../MathFactsGenerator/Settings/SettingsStore.tsx";
import { NewProblemsButton, RebuildForm } from "../MathFactsGenerator/Settings/WorksheetIdentity.tsx";
import { seedFromUrl } from "../MathFactsGenerator/UrlProcessor/ProcessUrl.tsx";
import { gradeName, subPackSheets } from "../MathFactsGenerator/SubPack.ts";
import { placementSheets } from "../MathFactsGenerator/PlacementCheck.ts";
import { CatchUpSection } from "./CatchUpSection.tsx";
import { standardText } from "../Shared/Seo/standards.ts";
import { useTranslation } from "../Shared/i18n/useTranslation.ts";

// Behind the lazy boundary like every other PDF surface: the renderer loads only for a page that
// shows a pack (bundleBoundary.test.js).
const WorksheetPreview = lazy(() => import("../MathFactsGenerator/PdfGenerator/WorksheetPreview.tsx"));

/**
 * A substitute pack page (ADR 0083): the grade's name and intro, one preview of the whole pack with
 * Print, Download and New problems, what is in it (each activity linking to the page that made it),
 * and the FAQ.
 *
 * It is not a generator page. The generator's rail describes one sheet, and on a pack it would change
 * nothing (ADR 0023's pairing rule), so there is no rail: the pack is the grade's, and each activity's
 * own page is where to change it. The seed controls are the generator's own, through the same
 * provider, so a pack rebuilds from the ID on its cover like any other sheet.
 *
 * scripts/prerender.js writes the same markup into the static HTML (`packContent`); change both. The
 * sheet's box is reserved there at the size it takes here, so nothing moves when the preview arrives.
 */
export function PackPage({ slug }: { slug: string }) {
    const entry = findBySlug(slug);
    const t = useTranslation();
    const canPreview = useCanPreviewPdf();
    const [seed, setSeed] = useState(() =>
        seedFromUrl(typeof window !== "undefined" ? window.location.search : "")
    );
    const settings = useMemo(() => cloneDeep(DefaultSettings), []);
    const placement = entry?.pack?.kind === "placement";
    const sheets = useMemo(() => {
        if (!entry?.pack) return [];
        return placement ? placementSheets(entry.pack, seed) : subPackSheets(entry.pack, seed);
    }, [entry, seed, placement]);

    if (!entry?.pack) {
        return null;
    }

    const kicker = FOOTER_GROUPS.find((group) => group.key === footerGroupOf(entry))?.heading;

    return (
        <>
            <Seo entry={entry} />
            <div className="gradeHub packPage">
                <header className="gradeHubHeader">
                    {kicker && <div className="kicker">{kicker}</div>}
                    <h1>{entry.h1}</h1>
                    {entry.intro?.map((paragraph, i) => (
                        <p key={i}>{paragraph}</p>
                    ))}
                </header>

                <SettingsProvider settings={settings} setSettings={noop} seed={seed} setSeed={setSeed}>
                    <div className="packSheet">
                        <Suspense fallback={null}>
                            <WorksheetPreview
                                settings={sheets[0].settings}
                                seed={seed}
                                sheets={sheets}
                                canPreview={canPreview}
                                fileName={`${gradeName(entry.pack.grade).replace(/ /g, "-")}-${placement ? "math-placement-check" : "substitute-math-pack"}.pdf`}
                                previewLabel={entry.h1}
                                eventParams={{ source: placement ? "placement_check" : "sub_pack", operation: gradeName(entry.pack.grade) }}
                                actions={<NewProblemsButton />}
                            />
                        </Suspense>
                    </div>
                    {/* Below the reserved box, not inside it: they render before the lazy preview does,
                        and inside the box the preview's arrival pushed them down the screen (CLS 0.022). */}
                    <div className="packIdentity">
                        <p className="packId">
                            {placement ? t("settings.checkIdPrefix") : t("settings.packIdPrefix")} <code>{seed}</code>
                        </p>
                        <RebuildForm />
                    </div>
                </SettingsProvider>

                {entry.objectives && entry.objectives.length > 0 && (
                    <div className="landingCovers">
                        <h3>{placement ? t("settings.checkCovers") : t("settings.packCovers")}</h3>
                        <ul>
                            {entry.objectives.map((objective) => (
                                <li key={objective}>{objective}</li>
                            ))}
                        </ul>
                        {entry.standards && entry.standards.length > 0 && (
                            <>
                                <h3>{t("workbench.standardsHeading")}</h3>
                                <ul className="landingStandards">
                                    {entry.standards.map((code) => (
                                        <li key={code}>
                                            <span className="standardCode">{code}</span> {standardText(code)}
                                        </li>
                                    ))}
                                </ul>
                            </>
                        )}
                    </div>
                )}

                <section className="gradeHubSection">
                    <h2>{placement ? t("settings.checkContents") : t("settings.packContents")}</h2>
                    <ul className="gradeHubTiles">
                        {entry.pack.activities.map((activity) => {
                            const target = findBySlug(activity.slug);
                            return (
                                <li key={activity.slug + activity.blurb}>
                                    <a className="gradeHubTile" href={activity.slug}>
                                        <span className="gradeHubTileTitle">{target?.h1 ?? activity.slug}</span>
                                        <span className="gradeHubTileBlurb">{activity.blurb}</span>
                                    </a>
                                </li>
                            );
                        })}
                    </ul>
                </section>

                {placement && <CatchUpSection pack={entry.pack} />}

                {entry.faq && entry.faq.length > 0 && (
                    <section className="landingFaq gradeHubFaq">
                        <h2>{t("settings.frequentlyAskedQuestions")}</h2>
                        {entry.faq.map((item) => (
                            <div className="faqItem" key={item.q}>
                                <h3>{item.q}</h3>
                                <p>{item.a}</p>
                            </div>
                        ))}
                    </section>
                )}
            </div>
        </>
    );
}

function noop() {}

export default PackPage;
